/**
 * Gestionnaire d'annonces pour les différents espaces
 * Permet au Super Admin de créer et gérer des annonces
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import RefreshButton from './ui/RefreshButton';
import ExportImportBar from './ExportImportBar';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { DatePickerFr } from './ui/DatePickerFr';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Megaphone, Plus, Edit, Trash2, Eye, RefreshCw, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import InstitutionalHero from './ui/InstitutionalHero';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Espaces disponibles
const SPACES = [
  { value: 'all', label: 'Tous les espaces', color: 'bg-gray-500' },
  { value: 'expert', label: 'Espace Expert', color: 'bg-blue-500' },
  { value: 'member', label: 'Espace Membre', color: 'bg-emerald-500' },
  { value: 'cooper', label: 'Espace COOPER\'S', color: 'bg-amber-500' },
  { value: 'public', label: 'Pages publiques', color: 'bg-purple-500' },
];

// Priorités
const PRIORITIES = [
  { value: 'low', label: 'Basse', color: 'bg-gray-400' },
  { value: 'normal', label: 'Normale', color: 'bg-blue-500' },
  { value: 'high', label: 'Haute', color: 'bg-amber-500' },
  { value: 'urgent', label: 'Urgente', color: 'bg-red-500' },
];

// Formulaire d'annonce
function AnnouncementForm({ announcement, onSave, onCancel }) {
  const [form, setForm] = useState({
    title: announcement?.title || '',
    content: announcement?.content || '',
    target_spaces: announcement?.target_spaces || ['all'],
    priority: announcement?.priority || 'normal',
    start_date: announcement?.start_date?.split('T')[0] || '',
    end_date: announcement?.end_date?.split('T')[0] || '',
    link_url: announcement?.link_url || '',
    link_text: announcement?.link_text || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const data = {
      ...form,
      start_date: form.start_date ? new Date(form.start_date).toISOString() : null,
      end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
    };
    
    await onSave(data);
    setSaving(false);
  };

  const toggleSpace = (space) => {
    setForm(prev => {
      let spaces = [...prev.target_spaces];
      if (space === 'all') {
        spaces = ['all'];
      } else {
        spaces = spaces.filter(s => s !== 'all');
        if (spaces.includes(space)) {
          spaces = spaces.filter(s => s !== space);
        } else {
          spaces.push(space);
        }
        if (spaces.length === 0) spaces = ['all'];
      }
      return { ...prev, target_spaces: spaces };
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Titre *</Label>
        <Input
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="Titre de l'annonce"
          required
        />
      </div>
      
      <div>
        <Label>Contenu *</Label>
        <Textarea
          value={form.content}
          onChange={(e) => setForm({ ...form, content: e.target.value })}
          placeholder="Contenu de l'annonce..."
          rows={4}
          required
        />
      </div>
      
      <div>
        <Label>Espaces cibles</Label>
        <div className="flex flex-wrap gap-2 mt-2">
          {SPACES.map(space => (
            <button
              key={space.value}
              type="button"
              onClick={() => toggleSpace(space.value)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                form.target_spaces.includes(space.value)
                  ? `${space.color} text-white`
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {space.label}
            </button>
          ))}
        </div>
      </div>
      
      <div>
        <Label>Priorité</Label>
        <div className="flex gap-2 mt-2">
          {PRIORITIES.map(p => (
            <button
              key={p.value}
              type="button"
              onClick={() => setForm({ ...form, priority: p.value })}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                form.priority === p.value
                  ? `${p.color} text-white`
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Date de début</Label>
          <DatePickerFr
            className="w-full mt-1"
            value={form.start_date}
            onChange={(v) => setForm({ ...form, start_date: v })}
          />
        </div>
        <div>
          <Label>Date de fin</Label>
          <DatePickerFr
            className="w-full mt-1"
            value={form.end_date}
            onChange={(v) => setForm({ ...form, end_date: v })}
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>URL du lien (optionnel)</Label>
          <Input
            value={form.link_url}
            onChange={(e) => setForm({ ...form, link_url: e.target.value })}
            placeholder="https://..."
          />
        </div>
        <div>
          <Label>Texte du lien</Label>
          <Input
            value={form.link_text}
            onChange={(e) => setForm({ ...form, link_text: e.target.value })}
            placeholder="En savoir plus"
          />
        </div>
      </div>
      
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit" disabled={saving}>
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : null}
          {announcement ? 'Mettre à jour' : 'Créer'}
        </Button>
      </div>
    </form>
  );
}

export default function AnnouncementsManager() {
  const { getAuthHeader } = useAuth();
  
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);

  useEffect(() => {
    fetchAnnouncements({ silent: true });
  }, []);

  const fetchAnnouncements = async ({ silent = false } = {}) => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/announcements`,
        { headers: getAuthHeader() }
      );
      setAnnouncements(res.data.announcements || []);
      if (!silent) {
        toast.success(`${(res.data.announcements || []).length} annonce(s) chargée(s)`);
      }
    } catch {
      toast.error("Erreur lors du chargement des annonces");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (data) => {
    try {
      if (editingAnnouncement) {
        await axios.put(
          `${BACKEND_URL}/api/admin/announcements/${editingAnnouncement.id}`,
          data,
          { headers: getAuthHeader() }
        );
      } else {
        await axios.post(
          `${BACKEND_URL}/api/admin/announcements`,
          data,
          { headers: getAuthHeader() }
        );
      }
      setShowForm(false);
      setEditingAnnouncement(null);
      fetchAnnouncements({ silent: true });
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette annonce ?')) return;
    
    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/announcements/${id}`,
        { headers: getAuthHeader() }
      );
      fetchAnnouncements({ silent: true });
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleActive = async (announcement) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/announcements/${announcement.id}`,
        { active: !announcement.active },
        { headers: getAuthHeader() }
      );
      fetchAnnouncements({ silent: true });
    } catch {
      toast.error('Erreur lors du changement de statut');
    }
  };

  const getPriorityBadge = (priority) => {
    const config = PRIORITIES.find(p => p.value === priority) || PRIORITIES[1];
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  const getSpaceBadges = (spaces) => {
    return spaces.map(s => {
      const space = SPACES.find(sp => sp.value === s);
      return space ? (
        <Badge key={s} variant="outline" className="text-xs">
          {space.label}
        </Badge>
      ) : null;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <InstitutionalHero
        icon={Megaphone}
        badge="Communication interne"
        title="Annonces & Communications"
        subtitle="Pilotez les annonces affichées aux utilisateurs (COOPER'S, EXPERTS, MEMBRES) : nouveautés produit, communiqués réglementaires, actualités institutionnelles."
        gradient="bg-gradient-to-br from-slate-900 via-rose-900 to-amber-900"
        actions={
          <>
            <ExportImportBar collection="annonces" label="Annonces" onImported={() => fetchAnnouncements({ silent: true })} />
            <RefreshButton
              onRefresh={async () => { await fetchAnnouncements({ silent: true }); }}
              loading={loading}
              successMessage={`${announcements.length} annonce(s) chargée(s)`}
              variant="light"
              testId="announcements-refresh-btn"
            />
            <Dialog open={showForm} onOpenChange={setShowForm}>
              <DialogTrigger asChild>
                <Button onClick={() => setEditingAnnouncement(null)} className="bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-bold shadow-md">
                  <Plus className="w-4 h-4 mr-2" /> Nouvelle annonce
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>
                    {editingAnnouncement ? 'Modifier l\'annonce' : 'Nouvelle annonce'}
                  </DialogTitle>
                </DialogHeader>
                <AnnouncementForm
                  announcement={editingAnnouncement}
                  onSave={handleSave}
                  onCancel={() => {
                    setShowForm(false);
                    setEditingAnnouncement(null);
                  }}
                />
              </DialogContent>
            </Dialog>
          </>
        }
      />

      {/* Liste des annonces */}
      {announcements.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Megaphone className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Aucune annonce créée</p>
            <Button className="mt-4" onClick={() => setShowForm(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Créer une annonce
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {announcements.map(announcement => (
            <Card key={announcement.id} className={!announcement.active ? 'opacity-60' : ''}>
              <CardContent className="py-4">
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-lg ${announcement.active ? 'bg-amber-100' : 'bg-gray-100'}`}>
                    <Megaphone className={`w-5 h-5 ${announcement.active ? 'text-amber-600' : 'text-gray-400'}`} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-gray-900">{announcement.title}</h3>
                      {getPriorityBadge(announcement.priority)}
                      {!announcement.active && (
                        <Badge variant="outline" className="text-gray-400">Désactivée</Badge>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">{announcement.content}</p>
                    
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      <div className="flex gap-1">{getSpaceBadges(announcement.target_spaces)}</div>
                      <span>•</span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {announcement.views_count || 0} vues
                      </span>
                      {announcement.link_url && (
                        <>
                          <span>•</span>
                          <a href={announcement.link_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-blue-500 hover:underline">
                            <ExternalLink className="w-3 h-3" />
                            {announcement.link_text || 'Lien'}
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={announcement.active}
                      onCheckedChange={() => toggleActive(announcement)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingAnnouncement(announcement);
                        setShowForm(true);
                      }}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => handleDelete(announcement.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
